import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Alignment, Content, ContentImage, ContentOrderedList, ContentStack, ContentTable, ContentText, Style, TableCell, TDocumentDefinitions, TFontDictionary } from 'pdfmake/interfaces';
import { Utils } from '../../core/utils';
import { LogService } from '../../logs/log.service';

@Injectable({
	providedIn: 'root'
})
export class PDFExportService {
	private docDefinition!: TDocumentDefinitions;
	private printerFriendlyMode = false;
	private readonly tagFontSizeMap: { [tag: string]: number } = { H1: 18.75, H2: 16.5, H3: 15, H4: 13.5, H5: 12, H6: 10.5 };

	constructor() { }
	
	setDocDefinition(docDef: TDocumentDefinitions, printerFriendlyMode = false) {
		docDef.pageMargins = docDef.pageMargins || [10, 10, 10, 20];

		const defaultStyle = docDef.defaultStyle = docDef.defaultStyle || {},
			styleDictionary = docDef.styles = docDef.styles || {},
			timestamp = new Date().toLocaleString('en-US', {
				month: 'long',
				day: '2-digit',
				year: 'numeric',
				hour: 'numeric',
				minute: 'numeric'
			});
		
		defaultStyle.font = defaultStyle.font || 'SourceSans';

		if (printerFriendlyMode) {
			styleDictionary.alarmText = styleDictionary.alarmText || {
				italics: true,
				alignment: 'right'
			};
		}
		else {
			styleDictionary.headerRow = styleDictionary.headerRow || {
				color: 'white',
				fillColor: '#1476ae',
				bold: true
			};
			styleDictionary.linkColor = styleDictionary.linkColor || {
				color: '#2fa4e7'
			};
			styleDictionary.headingColor = styleDictionary.headingColor || {
				color: '#317eac'
			};
			styleDictionary.alarmColor = {
				color: '#b94a48'
			};
			styleDictionary.alarmText = styleDictionary.alarmText || {
				color: styleDictionary.alarmColor.color,
				alignment: 'right'
			};
			styleDictionary.noteTemplateComment = {
				background: '#fef7b2',
				bold: true,
				italics: true
			};
			styleDictionary.outOfRange = {
				fillColor: '#fef7b2'
			};
			styleDictionary.verticalSegmentName = {
				margin: [0, 0, 0, 10]
			};
		}

		docDef.footer = docDef.footer || ((currentPage, pageCount) => ({
			columns: [
				{
					text: 'Created ' + timestamp,
					italics: true,
					font: 'Courier',
					fontSize: 10,
					margin: [10, 5, 0, 0]
				},
				{
					text: `Page ${currentPage} of ${pageCount}`,
					alignment: 'right',
					bold: true,
					italics: true,
					font: 'Courier',
					margin: [0, 5, 10, 0]
				}
			]
		}));

		this.docDefinition = docDef;
		this.printerFriendlyMode = printerFriendlyMode;
	}

	exportPDF() {
		const unicodeFontURL = `${window.location.origin}/webpack/fonts/Unicode-Upper.ttf`,
			fonts: TFontDictionary = {
				SourceSans: {
					normal: `${window.location.origin}/webpack/fonts/SourceSans-Regular.ttf`,
					bold: `${window.location.origin}/webpack/fonts/SourceSans-Bold.ttf`,
					italics: `${window.location.origin}/webpack/fonts/SourceSans-Italic.ttf`,
					bolditalics: `${window.location.origin}/webpack/fonts/SourceSans-BoldItalic.ttf`
				},
				Unicode: {
					normal: unicodeFontURL,
					bold: unicodeFontURL,
					italics: unicodeFontURL,
					bolditalics: unicodeFontURL
				},
				Times: {
					normal: 'Times-Roman',
					bold: 'Times-Bold',
					italics: 'Times-Italic',
					bolditalics: 'Times-BoldItalic'
				},
				Courier: {
					normal: 'Courier',
					bold: 'Courier-Bold',
					italics: 'Courier-Oblique',
					bolditalics: 'Courier-BoldOblique'
				}
			},
			fileName = `${LogService.selectedLog!.name} - ${Utils.getFileNameTimestamp()}`;
		
		pdfMake.createPdf(this.docDefinition, undefined, fonts, pdfFonts.pdfMake.vfs).download(fileName);
	}

	getRichTextCells(richTextElements: HTMLCollection | Element[]) {
		const richText: ContentStack = {
			alignment: 'left',
			stack: []
		};

		Array.from(richTextElements).forEach(rte => {
			if (richTextElements.length > 1 && rte.firstElementChild?.tagName == 'BR')
				richText.stack.push('\n');
			else if (rte.tagName == 'OL') {
				const ol: ContentOrderedList = {
					type: 'none',
					ol: []
				};

				const indentLevelIndexMap: { [indentLevel: number]: number } = {};
				for (let i = 0; i < 9; i++)
					indentLevelIndexMap[i] = 0;
				
				let previousIndentLevel: number;

				Array.from(rte.children).forEach(li => {
					const indentClass = Array.from(li.classList).find(c => c.startsWith('ql-indent')),
						indentLevel = indentClass && +indentClass.slice(-1) || 0,
						contentText = this.getCellsFromRichTextNode(li);
					
					if (indentLevel > previousIndentLevel)
						indentLevelIndexMap[indentLevel] = 1;
					else
						indentLevelIndexMap[indentLevel]++;
						
					const numberText =
						[1, 4, 7].includes(indentLevel) ? Utils.numberToLowerAlpha(indentLevelIndexMap[indentLevel] - 1) :
						[2, 5, 8].includes(indentLevel) ? Utils.numberToRomanNumeral(indentLevelIndexMap[indentLevel]) :
						indentLevelIndexMap[indentLevel].toString();
					
					(contentText.text as Content[]).unshift(numberText + '. ');
					
					ol.ol.push(contentText);

					previousIndentLevel = indentLevel;
				});

				richText.stack.push(ol);
			}
			else if (rte.tagName == 'UL') {
				richText.stack.push({
					ul: Array.from(rte.children).map(li => {
						const contentText = this.getCellsFromRichTextNode(li);

						return contentText;
					})
				});
			}
			else if (rte.tagName == 'TABLE') { // Currently only possible with Markdown
				const table = rte as HTMLTableElement,
					numColumns = Math.max(...Array.from(table.rows).map(tr => tr.cells.length)),
					tableBody: TableCell[][] = [];

				for (const tr of Array.from(table.rows)) {
					const tableCells: TableCell[] = [];

					for (let i = 0; i < numColumns; i++) {
						const cell = tr.cells[i];

						if (cell) {
							const contentText = this.getCellsFromRichTextNode(cell);

							cell.tagName == 'TH' ?
								contentText.bold = true :
								contentText.alignment = cell.getAttribute('align') as Alignment;

							tableCells.push(contentText);
						}
						else
							// Any <th> or <td> could be missing from the markdown
							tableCells.push('');
					}

					tableBody.push(tableCells);
				}

				richText.stack.push({
					table: {
						widths: Array(numColumns).fill('*'),
						body: tableBody
					},
					layout: {
						hLineWidth: (i, node) => .25,
						vLineWidth: (i, node) => .25,
						hLineColor: (i, node) => '#dddddd',
						vLineColor: (i, node) => '#dddddd'
					}
				});
			}
			else {
				const content = this.getCellsFromRichTextNode(rte);

				richText.stack.push(rte.tagName == 'BLOCKQUOTE' ?
					this.wrapInBlockquoteStyle(content) :
					content
				);

				rte.querySelectorAll('img').forEach(img => richText.stack.push(this.getImageContent(img)));
			}
		});

		return richText;
	}

	getNoteTemplateHeading(heading: Element): Content {
		return {
			text: this.emojifyText(heading.textContent!),
			style: 'headingColor',
			alignment: 'left',
			fontSize: this.tagFontSizeMap[heading.tagName],
		};
	}

	getInspectionNoteCells(templateElements: HTMLCollection): ContentStack {
		const templateCells: Content[] = [];

		Array.from(templateElements).forEach(te => {
			switch (te.tagName) {
				case 'TABLE':
					const trs = Array.from((te as HTMLTableElement).rows);

					if (trs.length)
						templateCells.push(this.getPDFTable(trs));
					else
						templateCells.push('\n');
					break;
				case 'DIV':
					Array.from(te.children as HTMLCollectionOf<HTMLElement>).forEach(ele => {
						switch (ele.tagName) {
							case 'DIV':
								if (ele.hasAttribute('data-template-comment')) {
									templateCells.push({
										text: ele.textContent!,
										style: 'noteTemplateComment'
									});
								}
								break;
							case 'SPAN':
								if (ele.hasAttribute('data-oor-msg')) {
									templateCells.push({
										text: ele.textContent!,
										style: 'alarmText'
									});
								}
								break;
							case 'A':
								if (ele.firstElementChild!.tagName == 'IMG') {
									const img = ele.firstElementChild as HTMLImageElement;

									templateCells.push(this.getImageContent(img));
								}
								else {
									templateCells.push({
										text: ele.textContent!,
										link: window.location.origin + ele.getAttribute('href'),
										style: 'linkColor'
									});
								}
								break;
						}
					});
					break;
				default:
					// <h1-h6> section level headings
					templateCells.push(this.getNoteTemplateHeading(te));
					break;
			}
		});			

		return {
			alignment: 'left',
			stack: templateCells
		};
	}

	private getPDFTable(trs: HTMLTableRowElement[]) {
		const pdfTable: ContentTable = {
			table: {
				widths: ['*', 'auto'],
				body: trs.map(tr => this.getPDFTableCellArray(tr))
			},
			layout: {
				hLineWidth: (i, node) => 0,
				vLineWidth: (i, node) => 0,
				paddingTop: (i, node) => 4,
				paddingBottom: (i, node) => 4
			}
		};

		return pdfTable;
	}

	private getPDFTableCellArray(tr: HTMLTableRowElement) {
		const verticallyRendered = tr.cells.length == 1,
			alignment = tr.cells[tr.cells.length - 1].getAttribute('align') as Alignment,
			fromRichTextEditor = tr.cells[tr.cells.length - 1].className == 'ql-editor';
		
			if (verticallyRendered) {
				const richTextElements = Array.from(tr.cells[0].children);
				
				const tableCell: TableCell = {
					colSpan: 2,
					stack: [
						{
							text: this.emojifyText(richTextElements.shift()!.textContent!.trim()),
							style: 'verticalSegmentName'
						},
						this.getRichTextCells(richTextElements)
					]
				};

				if (!fromRichTextEditor) {
					const cell: any = tableCell;
					cell.alignment = alignment; 

					for (const content of cell.stack[1].stack as ContentText[])
						content.margin = [0, 10, 0, 0];
				}
				
				return [tableCell, {}];
			}

		const segmentName = this.emojifyText(tr.cells[0].textContent!.trim()),
			tdElement = tr.cells[1].firstElementChild,
			tagName = tdElement?.tagName,
			getCellsAsRichText = fromRichTextEditor ||
				![undefined, 'A', 'P', 'SPAN'].includes(tagName) || // The span covers checkbox characters
				(tagName == 'P' && tdElement?.firstElementChild);
		
		if (getCellsAsRichText) {
			const cells = this.getRichTextCells(tr.cells[1].children);

			if (!fromRichTextEditor)
				cells.alignment = alignment;

			return [segmentName, cells];
		}

		const styles: string[] = [],
			chartLink = tr.cells[1].firstElementChild?.tagName == 'A',
			outOfRange = tr.cells[1].hasAttribute('data-oor-cell'),
			templateValueCell = {
				text: this.emojifyText(tr.cells[1].textContent!.trim()),
				alignment: alignment,
				style: styles,
				...chartLink && {
					link: `${window.location.origin}${tr.cells[1].firstElementChild!.getAttribute('href')}`
				}
			} as ContentText;

		chartLink && styles.push('linkColor');
		outOfRange && styles.push('outOfRange');

		return [segmentName, templateValueCell];
	}

	getWrappableCellText(text: string) {
		const regEx = /(\S{15})/g,
			textArray: Content[] = [];
		
		let lastIndex = 0, results;

		while ((results = regEx.exec(text)) != null) {
			textArray.push(
				{ text: this.emojifyText(text.substring(lastIndex, regEx.lastIndex)) },
				{ text: '\u200b', font: 'Unicode' } // zero-width space
			);

			lastIndex = regEx.lastIndex;
		}

		if (textArray.length) {
			const remainingText = text.substring(lastIndex);

			remainingText ? textArray.push(remainingText) : textArray.pop();

			return textArray;
		}
		
		return this.emojifyText(text);
	}

	/** Assigns Unicode font to any emoji characters that may be present in the text so that they can be rendered in the export */
	private emojifyText(text: string, styles?: Style) {
		const emojiRegEx = /\p{Extended_Pictographic}+/gu,
		emojiedText: Content[] = [];
	
		let lastIndex = 0, results;

		while ((results = emojiRegEx.exec(text)) != null) {
			emojiedText.push(
				text.substring(lastIndex, results.index),
				{
					text: text.substring(results.index, emojiRegEx.lastIndex),
					font: 'Unicode',
					style: styles
				}
			);

			lastIndex = emojiRegEx.lastIndex;
		}

		if (emojiedText.length) {
			const remainingText = text.substring(lastIndex);
			remainingText && emojiedText.push(remainingText);

			return emojiedText;
		}
		
		return text;
	}

	private getCellsFromRichTextNode(node: Node, content: ContentText = { text: [] }, styles: Style = {}, href?: string) {
		if (node.nodeName == '#text') {
			(content.text as Content[]).push({
				text: this.emojifyText(node.textContent!, styles),
				...href && {
					link: href,
					style: 'linkColor'
				},
				...styles
			});
		}
		else {
			const tag = node as HTMLElement;

			if (['P', 'LI', 'BLOCKQUOTE'].includes(tag.tagName)) {
				const classList = Array.from(tag.classList);

				if (classList.some(c => c == 'ql-align-right'))
					content.alignment = 'right';
				else {
					if (classList.some(c => c == 'ql-align-center'))
						content.alignment = 'center';
					
					const indentClass = classList.find(c => c.startsWith('ql-indent'));

					if (indentClass) {
						const leftPadding = +indentClass.slice(-1) * 1.5 * 14;
						content.margin = [leftPadding, 0, 0, 0];
					}
				}
			}
			else {
				switch (tag.tagName) {
					case 'STRONG':
						styles.bold = true;
						break;
					case 'EM':
						styles.italics = true;
						break;
					case 'S':
						styles.decoration = 'lineThrough';
						break;
					case 'U':
						// replaces any lineThrough style - API doesn't allow both at same time
						styles.decoration = 'underline';
						break;
					case 'A':
						href = tag.getAttribute('href')!;
						break;
					case 'SUP':
						styles.sup = true;
						break;
					case 'SUB':
						styles.sub = true;
						break;
					case 'H1':
					case 'H2':
					case 'H3':
					case 'H4':
					case 'H5':
					case 'H6':
						// Markdown heading - rich text editor uses .ql-size classes below
						styles.fontSize = this.tagFontSizeMap[tag.tagName];
						styles.color = this.docDefinition.styles?.headingColor?.color;
						break;
				}

				if (!this.printerFriendlyMode) {
					tag.style.color && (styles.color = Utils.rgbToHex(tag.style.color));
					tag.style.backgroundColor && (styles.background = Utils.rgbToHex(tag.style.backgroundColor));
				}

				if (!styles.font) {
					const fontClass = Array.from(tag.classList).find(c => c.startsWith('ql-font'));

					fontClass && (styles.font = fontClass == 'ql-font-serif' ? 'Times' : 'Courier');
				}

				if (!styles.fontSize) {
					const sizeClass = Array.from(tag.classList).find(c => c.startsWith('ql-size'));

					sizeClass && (styles.fontSize = ({
						'ql-size-small': 12,
						'ql-size-large': 20,
						'ql-size-huge': 24
					} as {[sizeClass: string]: number})[sizeClass]);
				}
			}

			tag.childNodes.forEach(node => {
				this.getCellsFromRichTextNode(node, content, { ...styles }, href);
			});
		}

		return content;
	}

	private wrapInBlockquoteStyle(content: ContentText): ContentTable {
		return {
			table: {
				body: [[content]],
				widths: '*'
			},
			layout: {
				hLineWidth: (i, node) => 0,
				paddingLeft: (i, node) => 16 * .75,
				vLineWidth: (i, node) => i == 0 ? 3 : 0,
				vLineColor: (i, node) => '#ccc'
			}
		};
	}

	private getImageContent(img: HTMLImageElement): ContentImage {
		this.docDefinition.images![img.src] = img.src;
		
		return {
			image: img.src,
			// If you specify width, image will scale proportionally
			width: this.docDefinition.pageOrientation == 'portrait' ? 250 : 400
		}
	}
}